/**
 * OfflineIndicator Component
 * Shows offline status, pending sales count, and sync status
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Wifi, 
  WifiOff, 
  CloudOff, 
  Cloud, 
  RefreshCw, 
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import syncManager from '../../utils/syncManager';
import offlineDB from '../../utils/offlineDB';

const OfflineIndicator = ({ onSyncComplete }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({
    isSyncing: false,
    pendingSales: 0,
    errorSales: 0,
    lastSyncTime: null
  });
  const [showDetails, setShowDetails] = useState(false);
  const mountedRef = useRef(false);
  const onSyncCompleteRef = useRef(onSyncComplete);
  
  // Keep ref updated
  useEffect(() => {
    onSyncCompleteRef.current = onSyncComplete;
  }, [onSyncComplete]);

  useEffect(() => {
    mountedRef.current = true;
    
    const fetchCounts = async () => {
      try {
        const counts = await offlineDB.getOfflineSalesCount();
        if (mountedRef.current) {
          setSyncStatus(prev => ({
            ...prev,
            pendingSales: counts.pending,
            errorSales: counts.error
          }));
        }
      } catch (e) {
        console.error('Error updating counts:', e);
      }
    };
    
    // Initialize sync manager
    syncManager.init();

    // Add listener for sync events
    const unsubscribe = syncManager.addListener((status) => {
      if (!mountedRef.current) return;
      
      if (status.type === 'online') {
        setIsOnline(true);
      } else if (status.type === 'offline') {
        setIsOnline(false);
      } else if (status.type === 'sync_start') {
        setSyncStatus(prev => ({ ...prev, isSyncing: true }));
      } else if (status.type === 'sync_complete') {
        setSyncStatus(prev => ({ 
          ...prev, 
          isSyncing: false,
          lastSyncTime: status.timestamp
        }));
        fetchCounts();
        if (status.synced > 0 && onSyncCompleteRef.current) {
          onSyncCompleteRef.current(status);
        }
      } else if (status.type === 'sync_error') {
        setSyncStatus(prev => ({ ...prev, isSyncing: false }));
        fetchCounts();
      }
    });

    // Listen for online/offline
    const handleOnline = () => {
      if (mountedRef.current) setIsOnline(true);
    };
    const handleOffline = () => {
      if (mountedRef.current) setIsOnline(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial and periodic status update
    fetchCounts();
    const interval = setInterval(fetchCounts, 10000);

    return () => {
      mountedRef.current = false;
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const handleManualSync = async () => {
    if (!isOnline || syncStatus.isSyncing) return;
    await syncManager.forceSyncNow();
  };

  const handleRetryFailed = async () => {
    await syncManager.retryFailedSales();
  };

  const totalPending = syncStatus.pendingSales + syncStatus.errorSales;
  const hasIssues = syncStatus.errorSales > 0;

  // Compact indicator for sidebar
  return (
    <div className="relative">
      {/* Main indicator button */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all relative ${
          !isOnline 
            ? 'bg-red-900/50 text-red-400' 
            : syncStatus.isSyncing
              ? 'bg-blue-900/50 text-blue-400'
              : totalPending > 0
                ? 'bg-orange-900/50 text-orange-400'
                : 'bg-slate-800 text-slate-400'
        }`}
        title={
          !isOnline 
            ? 'Sin conexión' 
            : syncStatus.isSyncing 
              ? 'Sincronizando...'
              : totalPending > 0
                ? `${totalPending} ventas pendientes`
                : 'Conectado'
        }
      >
        {!isOnline ? (
          <WifiOff className="w-5 h-5" />
        ) : syncStatus.isSyncing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : totalPending > 0 ? (
          <CloudOff className="w-5 h-5" />
        ) : (
          <Cloud className="w-5 h-5" />
        )}
        <span className="text-[9px]">
          {!isOnline ? 'Offline' : syncStatus.isSyncing ? 'Sync' : totalPending > 0 ? 'Pend.' : 'Online'}
        </span>
        
        {/* Badge for pending sales */}
        {totalPending > 0 && !syncStatus.isSyncing && (
          <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white rounded-full ${
            hasIssues ? 'bg-red-500' : 'bg-orange-500'
          }`}>
            {totalPending}
          </span>
        )}
      </button>

      {/* Details popup */}
      {showDetails && (
        <div className="absolute bottom-full left-0 mb-2 w-72 bg-slate-900 border border-slate-800 rounded-xl shadow-xl p-4 z-50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-white text-sm">Estado de Sincronización</h3>
            <button 
              onClick={() => setShowDetails(false)}
              className="text-slate-500 hover:text-white"
            >
              ×
            </button>
          </div>

          {/* Connection status */}
          <div className={`flex items-center gap-2 p-2 rounded-lg mb-3 ${
            isOnline ? 'bg-emerald-900/30' : 'bg-red-900/30'
          }`}>
            {isOnline ? (
              <>
                <Wifi className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm">Conectado</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm">Sin conexión</span>
              </>
            )}
          </div>

          {/* Pending sales */}
          {totalPending > 0 && (
            <div className="space-y-2 mb-3">
              {syncStatus.pendingSales > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Ventas pendientes:</span>
                  <span className="text-orange-400 font-bold">{syncStatus.pendingSales}</span>
                </div>
              )}
              {syncStatus.errorSales > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-red-400" />
                    Con errores:
                  </span>
                  <span className="text-red-400 font-bold">{syncStatus.errorSales}</span>
                </div>
              )}
            </div>
          )}

          {/* Sync status */}
          {syncStatus.isSyncing && (
            <div className="flex items-center gap-2 text-blue-400 text-sm mb-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Sincronizando ventas...</span>
            </div>
          )}

          {/* Last sync time */}
          {syncStatus.lastSyncTime && (
            <div className="text-xs text-slate-500 mb-3">
              Última sincronización: {new Date(syncStatus.lastSyncTime).toLocaleTimeString('es-MX')}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            {isOnline && totalPending > 0 && !syncStatus.isSyncing && (
              <button
                onClick={handleManualSync}
                className="flex-1 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded-lg flex items-center justify-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Sincronizar
              </button>
            )}
            {syncStatus.errorSales > 0 && (
              <button
                onClick={handleRetryFailed}
                className="flex-1 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded-lg flex items-center justify-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Reintentar
              </button>
            )}
          </div>

          {/* Offline mode info */}
          {!isOnline && (
            <div className="mt-3 p-2 bg-slate-800 rounded-lg">
              <p className="text-xs text-slate-400">
                <strong className="text-white">Modo Offline activo.</strong> Las ventas se guardarán localmente y se sincronizarán automáticamente cuando se restablezca la conexión.
              </p>
            </div>
          )}

          {/* Success message when all synced */}
          {isOnline && totalPending === 0 && !syncStatus.isSyncing && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              <span>Todo sincronizado</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
